
import React, { useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { Save } from "lucide-react";

interface HeroEditorProps {
  onSave: (data: any) => void;
}

interface HeroFormData {
  title_en: string;
  title_ar: string;
  subtitle_en: string;
  subtitle_ar: string;
  cta_en: string;
  cta_ar: string;
  image: string;
}

const HeroEditor: React.FC<HeroEditorProps> = ({ onSave }) => {
  const { language } = useLanguage();
  const [isSaving, setIsSaving] = useState(false);
  
  const form = useForm<HeroFormData>({
    defaultValues: {
      title_en: "Advancing Sustainable Development Goals in Palestine",
      title_ar: "تعزيز أهداف التنمية المستدامة في فلسطين",
      subtitle_en: "Working together to create a more sustainable future while addressing the unique challenges faced by Palestinian communities",
      subtitle_ar: "نعمل معًا لخلق مستقبل أكثر استدامة مع معالجة التحديات الفريدة التي تواجهها المجتمعات الفلسطينية",
      cta_en: "Explore Our Initiatives",
      cta_ar: "استكشف مبادراتنا",
      image: "/images/hero-background.jpg"
    },
  });
  
  const handleSubmit = (data: HeroFormData) => {
    setIsSaving(true);
    
    // Simulate API call to save data
    setTimeout(() => {
      setIsSaving(false);
      onSave(data);
    }, 1000);
  };
  
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardContent className="pt-6 space-y-4">
              <FormField
                control={form.control}
                name="title_en"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {language === "en" ? "Title (English)" : "العنوان (الإنجليزية)"}
                    </FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="subtitle_en"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {language === "en" ? "Subtitle (English)" : "العنوان الفرعي (الإنجليزية)"}
                    </FormLabel>
                    <FormControl>
                      <Textarea {...field} />
                    </FormControl>
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="cta_en"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {language === "en" ? "CTA Button (English)" : "زر الحث على العمل (الإنجليزية)"}
                    </FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6 space-y-4">
              <FormField
                control={form.control}
                name="title_ar"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {language === "en" ? "Title (Arabic)" : "العنوان (العربية)"}
                    </FormLabel>
                    <FormControl>
                      <Input {...field} dir="rtl" />
                    </FormControl>
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="subtitle_ar"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {language === "en" ? "Subtitle (Arabic)" : "العنوان الفرعي (العربية)"}
                    </FormLabel>
                    <FormControl>
                      <Textarea {...field} dir="rtl" />
                    </FormControl>
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="cta_ar"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {language === "en" ? "CTA Button (Arabic)" : "زر الحث على العمل (العربية)"}
                    </FormLabel>
                    <FormControl>
                      <Input {...field} dir="rtl" />
                    </FormControl>
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>
        </div>
        
        <Card>
          <CardContent className="pt-6">
            <FormField
              control={form.control}
              name="image"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {language === "en" ? "Hero Background Image URL" : "رابط صورة خلفية البطل"}
                  </FormLabel>
                  <FormControl>
                    <div className="space-y-4">
                      <Input {...field} />
                      {field.value && (
                        <div className="relative w-full h-40 rounded-md overflow-hidden border border-muted">
                          <img 
                            src={field.value} 
                            alt="Hero background" 
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.currentTarget.src = "/placeholder.svg";
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </FormControl>
                </FormItem>
              )}
            />
          </CardContent>
        </Card>
      
        <div className="flex justify-end">
          <Button 
            type="submit" 
            className="bg-palsustain-green hover:bg-palsustain-green/90"
            disabled={isSaving}
          >
            <Save className="mr-2 h-4 w-4" />
            {isSaving 
              ? (language === "en" ? "Saving..." : "جاري الحفظ...") 
              : (language === "en" ? "Save Changes" : "حفظ التغييرات")
            }
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default HeroEditor;
